import API_ENDPOINTS from './api/endpoints';

export const BUDGET_WORKSPACE_CACHE_TIME_MS = 24 * 60 * 60 * 1000;
export const BUDGET_WORKSPACE_STALE_TIME_MS = 30 * 1000;
export const MY_BUDGETS_CACHE_TIME_MS = 24 * 60 * 60 * 1000;

export const DEFAULT_MY_BUDGETS_PAYLOAD = Object.freeze({
  adv_search: false,
  search: '',
  parent_folder: null,
});

export const normalizeMyBudgetsPayload = payload => ({
  adv_search: Boolean(payload?.adv_search),
  search: String(payload?.search || ''),
  parent_folder: payload?.parent_folder ?? null,
});

export const getMyBudgetsQueryKey = payload => [
  'get-budget-details-data',
  normalizeMyBudgetsPayload(payload),
];

export const getBudgetByIdQueryKey = budgetId => [
  'get-budget-by-id',
  { budgetId },
];

export const getBudgetCategoryTreeQueryKey = budgetId => [
  'budget-category-tree',
  budgetId,
];

export const getBudgetCategoriesQueryKey = budgetId => [
  'budget-categories',
  budgetId,
];

export const getContractualChargesQueryKey = budgetId => [
  'get-contractual-charges',
  budgetId,
];

export const getFilmIncentivesQueryKey = budgetId => [
  'get-film-incentives',
  budgetId,
];

export const getSwimlaneQueryKey = payload => [
  'get-swimlanes-in-budget',
  {
    id: payload?.id,
    type: payload?.type,
  },
];

const responseWithData = (response, data) => ({
  ...(response || {}),
  data,
});

const setQueryDataIfMissing = (queryClient, queryKey, response, data) => {
  if (queryClient.getQueryData(queryKey) !== undefined) return;
  queryClient.setQueryData(queryKey, responseWithData(response, data));
};

export const buildBudgetCategoryTree = categories => {
  const categoryList = Array.isArray(categories) ? categories : [];
  const childrenByParentId = new Map();

  categoryList.forEach(category => {
    const parentId = category?.parent ?? null;
    const children = childrenByParentId.get(parentId) || [];
    children.push(category);
    childrenByParentId.set(parentId, children);
  });

  const hydrateCategory = (category, rootId, visitedIds) => {
    if (!category?.id || visitedIds.has(category.id)) return null;

    const nextVisitedIds = new Set(visitedIds);
    nextVisitedIds.add(category.id);
    const resolvedRootId = rootId || category.id;
    const children = (childrenByParentId.get(category.id) || [])
      .map(child => hydrateCategory(child, resolvedRootId, nextVisitedIds))
      .filter(Boolean);

    return {
      ...category,
      swimlane: resolvedRootId,
      budget_swimlane_lineitem: category?.parent ? [] : children,
      children: category?.parent ? children : [],
    };
  };

  return (childrenByParentId.get(null) || [])
    .map(category => hydrateCategory(category, category.id, new Set()))
    .filter(Boolean);
};

const seedCategoryDescendantQueries = (
  queryClient,
  category,
  response,
  depth
) => {
  if (!category?.id) return;

  const children =
    depth === 0
      ? category?.budget_swimlane_lineitem || []
      : category?.children || [];
  const childType = depth === 0 ? 'line-item' : 'line-item-children';

  setQueryDataIfMissing(
    queryClient,
    getSwimlaneQueryKey({ id: category.id, type: childType }),
    response,
    children
  );

  children.forEach(child =>
    seedCategoryDescendantQueries(queryClient, child, response, depth + 1)
  );
};

export const seedBudgetWorkspaceQueries = (
  queryClient,
  budgetId,
  treeResponse
) => {
  if (!budgetId || !Array.isArray(treeResponse?.data)) return;
  const rootCategories = buildBudgetCategoryTree(treeResponse.data);

  setQueryDataIfMissing(
    queryClient,
    getBudgetCategoriesQueryKey(budgetId),
    treeResponse,
    treeResponse.data
  );
  setQueryDataIfMissing(
    queryClient,
    getSwimlaneQueryKey({ id: budgetId, type: 'swimlane' }),
    treeResponse,
    rootCategories
  );

  rootCategories.forEach(category =>
    seedCategoryDescendantQueries(queryClient, category, treeResponse, 0)
  );
};

export const prefetchBudgetWorkspace = async (queryClient, budgetId) => {
  if (!budgetId) return;

  const treePromise = queryClient.fetchQuery(
    getBudgetCategoryTreeQueryKey(budgetId),
    () => API_ENDPOINTS.budgets.get_budget_category_tree(budgetId),
    {
      staleTime: BUDGET_WORKSPACE_STALE_TIME_MS,
      cacheTime: BUDGET_WORKSPACE_CACHE_TIME_MS,
    }
  );

  const supplementalPrefetches = [
    queryClient.prefetchQuery(
      getBudgetByIdQueryKey(budgetId),
      () => API_ENDPOINTS.budgets.get_budget_by_id(budgetId),
      {
        staleTime: BUDGET_WORKSPACE_STALE_TIME_MS,
        cacheTime: BUDGET_WORKSPACE_CACHE_TIME_MS,
      }
    ),
    queryClient.prefetchQuery(
      getContractualChargesQueryKey(budgetId),
      () => API_ENDPOINTS.budgets.get_contractual_charges(budgetId),
      {
        staleTime: BUDGET_WORKSPACE_STALE_TIME_MS,
        cacheTime: BUDGET_WORKSPACE_CACHE_TIME_MS,
      }
    ),
    queryClient.prefetchQuery(
      getFilmIncentivesQueryKey(budgetId),
      () => API_ENDPOINTS.budgets.get_film_incentives(budgetId),
      {
        staleTime: BUDGET_WORKSPACE_STALE_TIME_MS,
        cacheTime: BUDGET_WORKSPACE_CACHE_TIME_MS,
      }
    ),
  ];

  try {
    const treeResponse = await treePromise;
    seedBudgetWorkspaceQueries(queryClient, budgetId, treeResponse);
  } finally {
    await Promise.allSettled(supplementalPrefetches);
  }
};

export const prefetchOpenBudgetWorkspaces = async (
  queryClient,
  budgetIds,
  concurrency = 2
) => {
  const uniqueBudgetIds = [...new Set((budgetIds || []).filter(Boolean))];
  const batchSize = Math.max(1, concurrency);

  for (let index = 0; index < uniqueBudgetIds.length; index += batchSize) {
    const batch = uniqueBudgetIds.slice(index, index + batchSize);
    await Promise.allSettled(
      batch.map(budgetId => prefetchBudgetWorkspace(queryClient, budgetId))
    );
  }
};

export const prefetchMyBudgets = queryClient => {
  const payload = DEFAULT_MY_BUDGETS_PAYLOAD;
  return queryClient.prefetchQuery(
    getMyBudgetsQueryKey(payload),
    () => API_ENDPOINTS.budgets.get_budget_details_data(payload),
    {
      staleTime: 0,
      cacheTime: MY_BUDGETS_CACHE_TIME_MS,
    }
  );
};
